import { useRef } from 'react';

import Card from '@perpay-web/components/base/Card/Card';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import LegacyFocusLayout from '@perpay-web/components/composite/LegacyFocusLayout/LegacyFocusLayout';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import ResponsiveCopy from '@perpay-web/fintech/components/base/ResponsiveCopy/ResponsiveCopy';
import ResponsiveHeader from '@perpay-web/fintech/components/base/ResponsiveHeader/ResponsiveHeader';
import CopyIconButton from '@perpay-web/fintech/components/composite/CopyIconButton/CopyIconButton';
import CustomerServiceInfo from '@perpay-web/fintech/components/composite/CustomerServiceInfo/CustomerServiceInfo';
import PayrollInstructionsModalContainer from '@perpay-web/fintech/components/containers/PayrollInstructionsModalContainer';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { formatCurrency } from '@perpay-web/utils/stringUtils';

import styles from './CardManualPaymentSetup.scss';

const getInnerText = (element) => element.innerText;

const CardManualPaymentSetup = ({
    isLoading,
    onClickTryAgain,
    onClickDone,
    accountNumber,
    routingNumber,
    minimumPayment,
    amountToCharge = 0,
    directDepositFormUrl,
    onClickForm,
    onClickInstructions,
    onMount,
    onUnmount,
    onModalClose,
    isPinwheelEligible,
    showCustomerServiceInfo = false,
    isPHO = false,
}) => {
    useMountAndUnmount(() => {
        onMount();
        return onUnmount;
    });

    const { isModalOpen, setIsModalOpen } = useModal();

    const routingNumberRef = useRef(null);
    const accountNumberRef = useRef(null);
    const amountRef = useRef(null);
    const amount = amountToCharge || minimumPayment;

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    const renderFooterButtons = () =>
        isPinwheelEligible ? (
            <>
                <CardCta
                    Component={CustomLink}
                    mode='dark-inverse'
                    className={styles['footer-button-right-margin']}
                    onClick={onClickDone}
                    href={`${paths.dashboard.path}${isPHO ? '?PHO_complete=true' : ''}`}
                >
                    Done
                </CardCta>
                <CardCta Component={CustomButton} onClick={onClickTryAgain}>
                    Try Again
                </CardCta>
            </>
        ) : (
            <CardCta
                Component={CustomLink}
                onClick={onClickDone}
                href={`${paths.dashboard.path}${isPHO ? '?PHO_complete=true' : ''}`}
            >
                Done
            </CardCta>
        );

    const renderCopy = () =>
        isPinwheelEligible
            ? "Tap try again to set up direct deposit automatically or provide the information below to your employer's payroll team."
            : 'Enter the information below into your payroll account or provide it to your HR team for them to update your direct deposit.';

    return (
        <LegacyFocusLayout
            className={styles.container}
            footer={
                <div className={styles['footer-buttons']}>
                    {renderFooterButtons()}
                </div>
            }
        >
            <ResponsiveHeader>Direct deposit details</ResponsiveHeader>
            <ResponsiveCopy>{renderCopy()}</ResponsiveCopy>
            <Card className={styles.card}>
                <div>
                    <div className={styles.item}>
                        <div className={styles['item-header']}>
                            Routing Number
                        </div>
                        <div className={styles['item-content']}>
                            <span ref={routingNumberRef} id='routing-number'>
                                {routingNumber}
                            </span>
                            <CopyIconButton
                                textElementRef={routingNumberRef}
                                valueGetter={getInnerText}
                            />
                        </div>
                    </div>
                    <div className={styles.item}>
                        <div className={styles['item-header']}>
                            Account Number
                        </div>
                        <div className={styles['item-content']}>
                            <span ref={accountNumberRef} id='account-number'>
                                {accountNumber}
                            </span>
                            <CopyIconButton
                                textElementRef={accountNumberRef}
                                valueGetter={getInnerText}
                            />
                        </div>
                    </div>
                    <div className={styles.item}>
                        <div className={styles['item-header']}>Amount</div>
                        <div className={styles['item-content']}>
                            <span ref={amountRef} id='amount'>
                                {formatCurrency(amount)}
                            </span>
                            <CopyIconButton
                                textElementRef={amountRef}
                                valueGetter={getInnerText}
                            />
                        </div>
                    </div>
                    <div>
                        <div className={styles['item-header']}>
                            Account Type
                        </div>
                        <div className={styles['item-content']}>Checking</div>
                    </div>
                </div>
            </Card>
            <CustomerServiceInfo
                className={showCustomerServiceInfo ? '' : styles.hidden}
            />
            <div className={styles.help}>Other options</div>
            <ul className={styles.list}>
                <CustomLink
                    action='inline-link'
                    onClick={onClickForm}
                    href={directDepositFormUrl}
                    target='_blank'
                >
                    <li className={styles['list-item']}>
                        Provide a form for your payroll department
                    </li>
                </CustomLink>
                <CustomButton
                    action='inline-link'
                    onClick={() => {
                        onClickInstructions();
                        setIsModalOpen(true);
                    }}
                >
                    <li>Need some help? Tap here for instructions</li>
                </CustomButton>
            </ul>

            {isModalOpen ? (
                <PayrollInstructionsModalContainer
                    onClose={() => {
                        setIsModalOpen(false);
                        onModalClose();
                    }}
                    customAmount={amount}
                />
            ) : null}
        </LegacyFocusLayout>
    );
};

export default CardManualPaymentSetup;
